import { createFileRoute, useNavigate, Link, useHydrated } from "@tanstack/react-router";
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { getAuthenticatedHomePath } from "@/lib/authenticated-home";
import {
  attemptMemberSignup,
  MemberSignupAlreadyClaimedError,
  MemberSignupCodeRequiredError,
} from "@/lib/member-signup";
import { requestMemberSetupCode } from "@/lib/auth.functions";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";
import { pageHead } from "@/lib/seo";
import { m } from "@/paraglide/messages.js";

export const Route = createFileRoute("/auth")({
  // SSR renders null via useHydrated until the client mounts. That keeps the
  // sign-in form client-only without the ssr:false Lazy/Suspense hydration mismatch.
  head: () =>
    pageHead({
      title: m.auth_meta_title(),
      description: m.auth_meta_description(),
      path: "/auth",
      noindex: true,
    }),
  component: AuthPage,
});

type Mode = "signin" | "signup" | "forgot";

function AuthPage() {
  const hydrated = useHydrated();
  const navigate = useNavigate();
  const [mode, setMode] = useState<Mode>("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [setupCode, setSetupCode] = useState("");
  const [needsSetupCode, setNeedsSetupCode] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!hydrated) return;
    let cancelled = false;
    const redirectIfAuthed = async () => {
      let to = "/my-bookings";
      try {
        to = await getAuthenticatedHomePath();
      } catch {
        // Session may not be attached to serverFn yet; member default is safe.
      }
      if (cancelled) return;
      navigate({ to, replace: true });
    };

    supabase.auth.getSession().then(({ data }) => {
      if (data.session) void redirectIfAuthed();
    });
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event) => {
      if (event === "SIGNED_IN") void redirectIfAuthed();
    });
    return () => {
      cancelled = true;
      subscription.unsubscribe();
    };
  }, [navigate, hydrated]);

  if (!hydrated) return null;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "signup") {
        const result = await attemptMemberSignup({
          email,
          password,
          fullName: name,
          code: needsSetupCode ? setupCode : undefined,
        });
        toast.success(
          result.claimed ? m.auth_toast_account_ready() : m.auth_toast_account_created(),
        );
      } else if (mode === "signin") {
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw error;
      } else if (mode === "forgot") {
        const { error } = await supabase.auth.resetPasswordForEmail(email, {
          redirectTo: `${window.location.origin}/reset-password`,
        });
        if (error) throw error;
        toast.success(m.auth_toast_reset_sent());
        setMode("signin");
      }
    } catch (err) {
      if (err instanceof MemberSignupCodeRequiredError) {
        setNeedsSetupCode(true);
        toast.success(m.auth_toast_setup_code_sent());
      } else if (err instanceof MemberSignupAlreadyClaimedError) {
        toast.error(m.auth_error_already_claimed(), { duration: 8000 });
      } else {
        toast.error((err as Error).message);
      }
    } finally {
      setBusy(false);
    }
  };

  const resendSetupCode = async () => {
    setBusy(true);
    try {
      await requestMemberSetupCode({ data: { email } });
      toast.success(m.auth_toast_setup_code_sent());
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const tabs: { id: Mode; label: string }[] = [
    { id: "signin", label: m.auth_tab_signin() },
    { id: "signup", label: m.auth_tab_signup() },
  ];

  return (
    <section className="bg-ink page-band-pad">
      <div className="container-page">
        <div className="mx-auto max-w-md">
          <Link
            to="/"
            className="mb-6 inline-flex items-center gap-2 text-sm text-foreground/60 hover:text-ember"
          >
            <ArrowLeft className="h-4 w-4 rtl:rotate-180" /> {m.auth_back_home()}
          </Link>

          <div className="border border-white/10 bg-black/40 p-8">
            <h1 className="font-display text-3xl tracking-[0.08em] text-foreground">
              {mode === "forgot" ? m.auth_heading_forgot() : m.auth_heading_access()}
            </h1>
            <p className="mt-1 text-sm text-foreground/60">
              {mode === "forgot"
                ? m.auth_sub_forgot()
                : mode === "signup"
                  ? needsSetupCode
                    ? m.auth_sub_signup_code()
                    : m.auth_sub_signup()
                  : m.auth_sub_signin()}
            </p>

            {mode !== "forgot" && (
              <>
                <div className="mt-6 mb-4 flex border border-white/10">
                  {tabs.map((t) => (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => {
                        setMode(t.id);
                        setNeedsSetupCode(false);
                        setSetupCode("");
                      }}
                      className={`flex-1 px-2 py-2 text-xs uppercase tracking-[0.18em] transition-colors ${
                        mode === t.id
                          ? "bg-ember-fill text-primary-foreground"
                          : "text-foreground/60 hover:text-foreground"
                      }`}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>
              </>
            )}

            <form onSubmit={submit} className="space-y-3">
              {mode === "signup" && (
                <div>
                  <Label htmlFor="nm">{m.auth_label_full_name()}</Label>
                  <Input id="nm" value={name} onChange={(e) => setName(e.target.value)} required />
                </div>
              )}
              <div>
                <Label htmlFor="em">{m.auth_label_email()}</Label>
                <Input
                  id="em"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
              {(mode === "signin" || mode === "signup") && (
                <div>
                  <div className="flex items-center justify-between">
                    <Label htmlFor="pw">{m.auth_label_password()}</Label>
                    {mode === "signin" && (
                      <button
                        type="button"
                        onClick={() => setMode("forgot")}
                        className="text-xs text-ember hover:underline"
                      >
                        {m.auth_forgot_password()}
                      </button>
                    )}
                  </div>
                  <Input
                    id="pw"
                    type="password"
                    autoComplete={mode === "signup" ? "new-password" : "current-password"}
                    minLength={6}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />
                </div>
              )}

              {mode === "signup" && needsSetupCode && (
                <div>
                  <div className="flex items-center justify-between">
                    <Label htmlFor="setup-code">{m.auth_label_setup_code()}</Label>
                    <button
                      type="button"
                      onClick={() => void resendSetupCode()}
                      disabled={busy}
                      className="text-xs text-ember hover:underline"
                    >
                      {m.auth_btn_resend_setup_code()}
                    </button>
                  </div>
                  <Input
                    id="setup-code"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    pattern="\d{6}"
                    maxLength={6}
                    value={setupCode}
                    onChange={(e) => setSetupCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    required
                  />
                </div>
              )}

              <Button
                type="submit"
                disabled={busy}
                className="w-full bg-ember-fill text-primary-foreground hover:bg-ember-fill/90"
              >
                {mode === "signup" && m.auth_btn_create()}
                {mode === "signin" && m.auth_btn_signin()}
                {mode === "forgot" && m.auth_btn_send_reset()}
              </Button>
            </form>

            {mode === "forgot" && (
              <button
                type="button"
                onClick={() => setMode("signin")}
                className="mt-4 text-sm text-foreground/60 hover:text-ember"
              >
                {m.auth_back_to_signin()}
              </button>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
